/**
 * @progression/host-testui (L6) — the functional test harness
 * (UI_Instructions/TestUI_Architecture_v01.md).
 *
 * A single deployable page that wires every built element together so the whole
 * background can be operated and tested once deployed. It composes the existing
 * frozen seams behind the swappable renderer — a renderer + control-panel problem,
 * with the engine immovable beneath it. It imports app / view / i18n / host-bundle
 * / contracts ONLY — never `@progression/core` (INV-DEP) — so it is structurally
 * unable to change a verdict (INV-CORE / INV-STABLE-ID).
 *
 * Targets (TestUI §5): Cloudflare (STRONG isolate; compute degrades to
 * `unavailable` cleanly) is first; the in-browser / webapp realms run real
 * Pyodide. The same recorded fact re-derives the same verdict everywhere
 * (INV-CONFORM) — the harness surfaces this in its status bar.
 */

import { CONTRACT_VERSION } from '@progression/host-bundle';
import { Harness, mountHarness } from './harness.js';
import type { HarnessOptions, ThemeName } from './harness.js';
import { createHarnessAdapter, STORAGE_KEY } from './adapter.js';

export const HOST_TARGET = 'testui' as const;

export { Harness, mountHarness, createHarnessAdapter, STORAGE_KEY };
export type { HarnessOptions, ThemeName };

export interface BootOptions {
  /** DOM element or selector to paint into; default `#harness-root`. */
  readonly mountPoint?: HTMLElement | string;
  /** Initial locale (any shipped catalog); default `en`. */
  readonly locale?: string;
  /** Honest isolation tier to report; default BEST-EFFORT (STRONG on Cloudflare). */
  readonly isolationTier?: 'STRONG' | 'BEST-EFFORT';
}

/**
 * Boot the harness. When a DOM is present it mounts and paints the live stack
 * (defaulting to the WCAG-AA dark theme); under Node (no DOM) it returns an
 * unmounted `Harness` so headless tests can still drive the store + queries.
 */
export function boot(options: BootOptions = {}): Harness {
  const tier = options.isolationTier ?? 'BEST-EFFORT';
  const harnessOptions: HarnessOptions = {
    adapter: createHarnessAdapter({ tier }),
    isolationTier: tier,
    ...(options.locale ? { locale: options.locale } : {}),
    ...(options.mountPoint ? { mountPoint: options.mountPoint } : {}),
  };
  const harness = new Harness(harnessOptions);
  if (typeof document !== 'undefined') {
    harness.mount(options.mountPoint ?? '#harness-root');
  }
  return harness;
}

/** Convenience for a DOM page (mirrors `mountHarness` with the localStorage adapter). */
export function bootInBrowser(options: BootOptions = {}): Harness {
  return mountHarness({
    adapter: createHarnessAdapter(),
    ...(options.locale ? { locale: options.locale } : {}),
    ...(options.mountPoint ? { mountPoint: options.mountPoint } : {}),
    ...(options.isolationTier ? { isolationTier: options.isolationTier } : {}),
  });
}

export function describeHost(): string {
  return `host-testui · functional test harness · dark default (light/high-contrast selectable) · all shipped locales + RTL · L3 contract v${CONTRACT_VERSION}`;
}
